import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronLeft, ExternalLink, FileText, Search, X } from 'lucide-react';
import api from '../../services/api.js';
import { assetUrl } from '../../utils/assetUrl.js';
import BrandLoading from '../../components/ui/BrandLoading.jsx';
import ScrollReveal from '../../components/public/ScrollReveal.jsx';
import SectionAmbientGradient from '../../components/public/SectionAmbientGradient.jsx';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import {
  getNormatividadCategoryLabel,
  groupNormatividadByCategory,
} from '../../constants/normatividadCategories.js';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
}

function DocumentRow({ doc, delayMs }) {
  return (
    <ScrollReveal delayMs={delayMs}>
      <li>
        <a
          href={assetUrl(doc.documento)}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-col gap-4 px-5 py-5 transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between sm:px-6"
        >
          <div className="flex min-w-0 items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0d2b7f]/10 text-[#0d2b7f] ring-1 ring-[#0d2b7f]/15">
              <FileText className="h-6 w-6" strokeWidth={1.75} aria-hidden />
            </span>
            <div className="min-w-0">
              <h3 className="font-display text-lg font-semibold text-slate-800 transition group-hover:text-[#0d2b7f]">
                {doc.titulo}
              </h3>
              <p className="mt-1 flex items-center gap-2 text-sm text-slate-500">
                <Calendar className="h-3.5 w-3.5 text-[#00A63E]" aria-hidden />
                Actualizado: {formatDate(doc.fecha_actualizacion)}
              </p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-2 self-start rounded-lg border border-[#1acb12]/35 bg-brand-green px-4 py-2 text-sm font-semibold text-white transition group-hover:bg-brand-green-dark sm:self-center">
            Ver documento
            <ExternalLink className="h-4 w-4" aria-hidden />
          </span>
        </a>
      </li>
    </ScrollReveal>
  );
}

function matchesSearch(doc, query) {
  if (!query) return true;
  const haystack = [
    doc.titulo,
    getNormatividadCategoryLabel(doc.categoria),
    formatDate(doc.fecha_actualizacion),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

export default function NormatividadPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const query = debouncedSearch.trim().toLowerCase();

  const filteredItems = useMemo(
    () => items.filter((doc) => matchesSearch(doc, query)),
    [items, query]
  );

  const sections = useMemo(() => groupNormatividadByCategory(filteredItems), [filteredItems]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: res } = await api.get('/v1/public/normatividad');
        if (cancelled) return;
        const raw = res?.data;
        setItems(Array.isArray(raw) ? raw : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'No se pudieron cargar los documentos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="pb-20">
      <section className="relative overflow-hidden border-b border-white/10">
        <SectionAmbientGradient />
        <div className="relative mx-auto max-w-7xl px-6 py-14 lg:px-8 lg:py-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent-lime transition hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            Volver al inicio
          </Link>
          <ScrollReveal className="mt-10 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">Transparencia</p>
            <h1 className="mt-4 font-display text-4xl font-bold text-white md:text-5xl">Normatividad</h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              Documentos agrupados por tipo: documentos de asamblea y reglamentos de la institución. Cada archivo indica
              su fecha de última actualización.
            </p>
          </ScrollReveal>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {loading && <BrandLoading variant="inline" tone="light" label="Cargando documentos…" className="min-h-[24vh]" />}

          {error && (
            <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
          )}

          {!loading && !error && items.length === 0 && (
            <p className="text-center text-slate-600">No hay documentos publicados por el momento.</p>
          )}

          {!loading && !error && items.length > 0 && (
            <div className="mb-10">
              <label htmlFor="normatividad-buscar" className="sr-only">
                Buscar documentos
              </label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                  aria-hidden
                />
                <input
                  id="normatividad-buscar"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por título o categoría…"
                  className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-12 pr-12 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-brand-green focus:ring-2 focus:ring-brand-green/25"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Limpiar búsqueda"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
              {query ? (
                <p className="mt-3 text-sm text-slate-500">
                  {filteredItems.length === 0
                    ? 'No se encontraron documentos con ese criterio.'
                    : `${filteredItems.length} documento${filteredItems.length === 1 ? '' : 's'} encontrado${filteredItems.length === 1 ? '' : 's'}.`}
                </p>
              ) : null}
            </div>
          )}

          {!loading && !error && items.length > 0 && query && filteredItems.length === 0 && (
            <p className="text-center text-slate-600">No hay resultados para tu búsqueda. Prueba con otras palabras.</p>
          )}

          {!loading && !error && sections.length > 0 && (
            <div className="space-y-14">
              <nav className="flex flex-wrap gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4" aria-label="Ir a sección">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#normatividad-${section.id}`}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-[#1acb12]/40 hover:text-[#0d2b7f]"
                  >
                    {section.label}
                  </a>
                ))}
              </nav>

              {sections.map((section, sectionIndex) => (
                <ScrollReveal key={section.id} delayMs={sectionIndex * 60}>
                  <div id={`normatividad-${section.id}`} className="scroll-mt-28">
                    <h2 className="font-display text-2xl font-bold text-accent-blue">{section.label}</h2>
                    <p className="mt-2 text-slate-600">{section.description}</p>
                    <ul className="mt-6 divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      {section.documentos.map((doc, i) => (
                        <DocumentRow key={doc.id} doc={doc} delayMs={Math.min(i, 4) * 40} />
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}

          {!loading && !error && items.length > 0 && sections.length === 0 && (
            <p className="text-center text-slate-600">
              Hay registros sin categoría válida. Contacta al administrador del portal.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
